import type { ContainerNode, SnippetNode, WorkflowNode } from '../../nodes';
import {
  getLayoutContainerId,
  isContainerNode,
  isFolderNodeId,
  isSnippetInternalChildNode,
} from '../../nodes';
import {
  CANVAS_HORIZONTAL_PADDING,
  CANVAS_VERTICAL_PADDING,
  COLUMN_SPACING,
  NODE_HEIGHT,
  NODE_WIDTH,
  ROW_SPACING,
} from '../../constants';
import type { MaxPathLengthMap } from '../types';
import type {
  Allocation,
  NodeColumn,
  NodeDimensions,
  NodeMatrix,
  NodePosition,
} from './types';
import { defaultAllocation, defaultNodeDimensions } from './constants';
import { isActionIdOfType } from 'flow/service/actions';
import { ActionType } from 'flow/store/engine/constants';

/**
 * convert column and row numbers to pixels using the predefined dimensions
 */
export function getNodePosition(args: { row: number; column: number }): WorkflowNode['position'] {
  const { row, column } = args;

  const x = CANVAS_HORIZONTAL_PADDING + (column * (NODE_WIDTH + COLUMN_SPACING));
  const y = CANVAS_VERTICAL_PADDING + (row * (NODE_HEIGHT + ROW_SPACING));

  return { x, y };
}

export function getNextAvailableRowIndex(
  column: NodeColumn,
  node: WorkflowNode,
  isFloated?: boolean,
) {
  const requiresOwnership = (
    (getLayoutContainerId(node) !== null)
    || isContainerNode(node)
    || isFloated
  );
  const comparator = requiresOwnership
    ? (allocation: Allocation) => isReservedForNode(allocation, node)
    : isFree;

  for (let rowIndex = 0; rowIndex < column.length; rowIndex += 1) {
    if (comparator(column[rowIndex])) {
      return rowIndex;
    }
  }

  return -1;
}

function maxOccurrences(values: Iterable<number>): number {
  const frequencyMap = new Map<number, number>();
  let max = 0;

  for (const v of values) {
    const next = (frequencyMap.get(v) ?? 0) + 1;
    frequencyMap.set(v, next);
    if (next > max) max = next;
  }

  return max;
}

/** Naively gets node dimensions of a graph, assuming every node has has a dimension of 1x1. */
export function getDimensions(args: { maxPathLengthMap: MaxPathLengthMap }): NodeDimensions;

/** Gets node dimensions of a graph, taking into account nodes that span multiple dimensions. */
export function getDimensions(args: {
  maxPathLengthMap: MaxPathLengthMap;
  nodeIds: WorkflowNode['id'][];
  subGraphDimensions: Record<ContainerNode['id'], NodeDimensions>;
}): NodeDimensions;

export function getDimensions(args: {
  maxPathLengthMap: MaxPathLengthMap;
  nodeIds?: WorkflowNode['id'][];
  subGraphDimensions?: Record<ContainerNode['id'], NodeDimensions>;
}): NodeDimensions {
  const { nodeIds, maxPathLengthMap, subGraphDimensions } = args;

  const longestPath = Math.max(...maxPathLengthMap.values());

  // naive calculation of subgraph dimensions where all nodes are assumed to be 1x1
  if (!nodeIds || !subGraphDimensions) {
    const maxRows = maxOccurrences(maxPathLengthMap.values());
    return {
      nodeWidth: longestPath + 1,
      nodeHeight: maxRows + 1,
    };
  }

  // calculate subgraph-aware dimensions
  const columnHeights = new Map<number, number>();

  for (const id of nodeIds) {
    const columnIndex = maxPathLengthMap.get(id);

    // if container node, add its row/height demand to all columns it spans
    const isContainerNodeId = isFolderNodeId(id) || isActionIdOfType(id, ActionType.SNIPPET);
    if (isContainerNodeId) {
      const rowDemand = Math.max(1, subGraphDimensions[id].nodeHeight);
      const columnEnd = columnIndex + subGraphDimensions[id].nodeWidth;

      for (let containerCol = columnIndex; containerCol < columnEnd; containerCol += 1) {
        const columnHeight = columnHeights.get(containerCol) ?? 0;
        columnHeights.set(containerCol, columnHeight + rowDemand);
      }

      // eslint-disable-next-line no-continue
      continue;
    }

    // otherwise, just add additional height for this 1x1 node
    const columnHeight = columnHeights.get(columnIndex) ?? 0;
    columnHeights.set(columnIndex, columnHeight + 1);
  }

  return {
    nodeWidth: longestPath + 1,
    nodeHeight: columnHeights.size ? Math.max(...columnHeights.values()) : 1,
  };
}

export function createDefaultNodeMatrix(dimensions: NodeDimensions): NodeMatrix {
  const { nodeWidth, nodeHeight } = dimensions;

  return Array.from({ length: nodeWidth }, () => (
    Array.from({ length: nodeHeight }, () => structuredClone(defaultAllocation))
  ));
}

function isReservedForNode(allocation: Allocation, node: WorkflowNode) {
  const { owningId, allocatedNodeId } = allocation;
  const layoutId = getLayoutContainerId(node);
  if (layoutId !== null) {
    return !allocatedNodeId && (owningId === layoutId);
  }
  return !allocatedNodeId && (owningId === node.id);
}

function isFree(allocation: Allocation) {
  const { owningId, allocatedNodeId } = allocation;
  return !owningId && !allocatedNodeId;
}

/**
 * Returns whether or not a space is free in the postion matrix.
 * The node position is the top-left corner of the space described by the supplied node dimensions.
 */
export function isSpaceFree(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
}): boolean {
  const { matrix, position, dimensions } = args;

  const { column: startCol, row: startRow } = position;
  const { nodeWidth, nodeHeight } = dimensions;

  // Check all positions the node would occupy
  for (let col = startCol; col < startCol + nodeWidth; col += 1) {
    // out of bounds
    if (col >= matrix.length) return false;

    const rows = matrix[col];

    for (let row = startRow; row < startRow + nodeHeight; row += 1) {
      // out of bounds
      if (row >= rows.length) return false;

      // space is already occupied, space is not free
      if (rows[row].owningId) return false;
    }
  }

  return true;
}


/** Mutates the position matrix, marking the owning node id in each position */
function assignOwningNode(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
  ownerId: WorkflowNode['id'];
}) {
  const {
    matrix,
    position,
    dimensions,
    ownerId,
  } = args;

  const { column: startCol, row: startRow } = position;
  const { nodeWidth, nodeHeight } = dimensions;

  const endCol = startCol + nodeWidth;
  const endRow = startRow + nodeHeight;

  for (let col = startCol; col < endCol; col += 1) {
    for (let row = startRow; row < endRow; row += 1) {
      matrix[col][row].owningId = ownerId;
    }
  }
}

/**
 * Get the column for a node.
 * Columns are determined by the largest dependent path a node might have.
 */
export function getNodeColumn(
  maxPathLengthMap: MaxPathLengthMap,
  node: WorkflowNode,
): number {
  if (isSnippetInternalChildNode(node)) {
    // remove snippet parent action from column computation - required for
    // nodes that contain snippet gate rules
    return maxPathLengthMap.get(node.id) - 1;
  }
  return maxPathLengthMap.get(node.id);
}

/**
 * Creates a position matrix, marking subgraph spaces as owned by their container nodes.
 */
export function allocateSubgraphOwnership(args: {
  matrix: NodeMatrix;
  nodes: WorkflowNode[];
  maxIncomingPathLengthMap: MaxPathLengthMap;
  subGraphDimensions: Record<SnippetNode['id'], NodeDimensions>;
  floatedAnchorNodeId?: WorkflowNode['id'];
}): NodeMatrix {
  const {
    matrix,
    nodes,
    maxIncomingPathLengthMap,
    subGraphDimensions,
    floatedAnchorNodeId,
  } = args;

  const floatedNode = floatedAnchorNodeId
    ? nodes.find(n => n.id === floatedAnchorNodeId)
    : undefined;
  if (floatedNode) {
    const column = getNodeColumn(maxIncomingPathLengthMap, floatedNode);
    assignOwningNode({
      matrix,
      position: { column, row: 0 },
      dimensions: defaultNodeDimensions,
      ownerId: floatedNode.id,
    });
  }

  nodes.forEach(node => {
    if (!isContainerNode(node)) {
      // only mark spaces owned by node containers
      return;
    }
    const dimensions = subGraphDimensions[node.id];
    const column = getNodeColumn(maxIncomingPathLengthMap, node);

    let row = 0;
    while (!isSpaceFree({ matrix, position: { column, row }, dimensions })) {
      row += 1;
    }

    assignOwningNode({
      matrix,
      position: { column, row },
      dimensions,
      ownerId: node.id,
    });
  });

  return matrix;
}

export function allocate(args: {
  matrix: NodeMatrix;
  position: NodePosition;
  dimensions: NodeDimensions;
  nodeId: WorkflowNode['id'];
}) {
  const {
    matrix,
    position,
    dimensions,
    nodeId,
  } = args;
  const { column, row } = position;

  const colEnd = column + dimensions.nodeWidth;
  const rowEnd = row + dimensions.nodeHeight;

  for (let rowIndex = row; rowIndex < rowEnd; rowIndex += 1) {
    for (let colIndex = column; colIndex < colEnd; colIndex += 1) {
      matrix[colIndex][rowIndex].allocatedNodeId = nodeId;
    }
  }
}

export function getSnippetNodeDimensions(
  subGraphDimensions: NodeDimensions,
): NodeDimensions {
  return { nodeWidth: subGraphDimensions.nodeWidth, nodeHeight: 1 };
}
